import {getAppEditorStore} from "./appEditorStore";
import {getStore, useStore} from "./index";
import {getModalStore} from "./ModalStore";
import {findOneDepthParent} from "../editor";

class ActionStack {
    /** @type {object} */
    before;
    /** @type {object} */
    after;

    /**
     * ActionStack 생성자
     * @param {object?} before 이전 상태
     * @param {object?} after 변경된 상태
     */
    constructor(before, after) {
        this.before = before;
        this.after = after;
    }

    /**
     * 오버라이딩을 위한 virtual function
     * @return {Promise<any>}
     */
    async cancel() {
    };

    /**
     * 대상이 되는 컴포넌트를 가져오는 함수
     * selectorType 에 따라 부모 컴포넌트를 먼저 찾고 해당 컴포넌트를 찾거나 바로 해당 컴포넌트를 찾는다
     * @param {string} connectedBoardId 연결보드의 고유 아이디
     * @param {string} selectorType 셀렉터를 구분하기 위한 타입
     * @param {string} selector 선택자(html className)
     * @param {number} selectorIdx 해당 컴포넌트의 인덱스
     * @param {string} parentSelector 부모 컴포넌트의 선택자
     * @param {string} parentIdx 부모 컴포넌트의 인덱스
     * @return {import('grapesjs').Component}
     */
    getTargetComponent({connectedBoardId, selectorType, selector, selectorIdx, parentSelector, parentIdx}) {
        const higherComponent = getAppEditorStore().componentList.find((component) => component.attributes.attributes['data-id'] === connectedBoardId);
        if (['textTag', 'convertedImg'].includes(selectorType)) {
            const parentComponents = higherComponent.find(parentSelector);
            const parentComponent = parentComponents[parentIdx];
            const components = parentComponent.find(selector);
            return components[selectorIdx]
        } else {
            if (parentSelector) {
                const parentComponents = higherComponent.find(parentSelector);
                const parentComponent = parentComponents[parentIdx];
                const components = parentComponent.find(selector);
                return components[selectorIdx]
            } else {
                const components = higherComponent.find(selector);
                return components[selectorIdx];
            }
        }
    }
}

export class AddStack extends ActionStack {
    async cancel() {
        const connectedBoardId = this.after['connectedBoardId'];
        /** @type {import('grapesjs').Component} */
        const component = getAppEditorStore().componentList.find(component => component.attributes.attributes['data-id'] === connectedBoardId);
        const {deleteInfos, deleteTrees} = await getAppEditorStore().dropConnectedBoard(component.index(), true);
        getAppEditorStore().boardTabChk = false;
        return new DeleteStack({
            connectedBoardInfo: deleteInfos[0].connectedBoardInfo,
            componentIndex: deleteInfos[0].componentIndex,
            boardTree: deleteTrees[0]
        }, null);
    }
}

export class DeleteStack extends ActionStack {
    async cancel() {
        /** @type {{connectedBoardInfo:ResponseBoard,componentIndex:number,boardTree:object}} */
        const {connectedBoardInfo, componentIndex, boardTree} = this.before;
        const connectedBoardId = connectedBoardInfo.connectedBoardId;
        if (connectedBoardInfo.type === 'COMMON') {
            connectedBoardInfo._id = connectedBoardInfo.commonBoardOrigin;
        }
        await getAppEditorStore().processAddBoard(connectedBoardInfo, boardTree, componentIndex, connectedBoardId);

        return new AddStack(null, {connectedBoardId});
    }
}

export class AddMultiStack extends ActionStack {
    async cancel() {
        const connectedBoardIdList = this.after['connectedBoardIdList'];
        // let deleteInfos, deleteTrees;
        const deleteInfos = await getAppEditorStore().deletePublicInfoFromBoard(connectedBoardIdList);
        const deleteTrees = getAppEditorStore().boardTree.filter(tree => connectedBoardIdList.includes(tree.data['data-id']));
        getAppEditorStore().setBoardTree(getAppEditorStore().boardTree.filter(tree => !connectedBoardIdList.includes(tree.data['data-id'])));
        getAppEditorStore().componentList.filter((component) => connectedBoardIdList.includes(component.attributes.attributes['data-id'])).forEach(comp => {
            comp.destroy();
        });
        getAppEditorStore().relocateHighlighter();
        getAppEditorStore().refreshComponentList();
        getAppEditorStore().editor.Commands.run('reload-scripts');
        if (!(getAppEditorStore().componentList.find(component => component.attributes.attributes['data-id'] === connectedBoardIdList[0]))) {
            getAppEditorStore().boardTabChk = false;
        }
        return new DeleteMultiStack({deleteInfos, deleteTrees}, null);
    }
}

export class DeleteMultiStack extends ActionStack {
    async cancel() {
        const deleteInfos = this.before.deleteInfos;
        const deleteTrees = this.before.deleteTrees;
        const connectedBoardIdList = [];
        for (const {connectedBoardInfo, componentIndex} of deleteInfos) {
            const connectedBoardId = connectedBoardInfo.connectedBoardId;
            const tree = deleteTrees.find(node => node.data['data-id'] === connectedBoardId);
            await getAppEditorStore().processAddBoard(connectedBoardInfo, tree, componentIndex, connectedBoardId);
            connectedBoardIdList.push(connectedBoardId);
        }
        getAppEditorStore().refreshComponentList();
        return new AddMultiStack(null, {connectedBoardIdList});
    }
}

export class UnlockCommonBoardStack extends ActionStack {
    async cancel() {
        const appEditorStore = getAppEditorStore();
        const {connectedBoardId} = this.before;
        const component = appEditorStore.componentList.find(comp => comp.attributes.attributes['data-id'] === connectedBoardId);
        appEditorStore.removeConnectedBoardCssRules(component);
        component.destroy();
        await appEditorStore.addCommonBoard(this.before, connectedBoardId);
        const tree = appEditorStore.boardTree.find(node => node.data['data-id'] === connectedBoardId);
        tree.data.type = 'COMMON';
        delete tree.data.checked;
        appEditorStore.refreshComponentList();
        appEditorStore.editor.Commands.run('reload-scripts');
        appEditorStore.relocateHighlighter();
        return new RewindUnlockCommonBoardStack(this.after, this.before);
    }
}

export class RewindUnlockCommonBoardStack extends ActionStack {
    async cancel() {
        const appEditorStore = getAppEditorStore();
        const {connectedBoardId, isSet} = this.before;
        const component = getAppEditorStore().componentList.find(comp => comp.attributes.attributes['data-id'] === connectedBoardId);
        appEditorStore.removeConnectedBoardCssRules(component);
        component.destroy();
        if (isSet) {
            await appEditorStore.addClipBoardSet(this.before, connectedBoardId);
        } else {
            await appEditorStore.addClipBoard(this.before, connectedBoardId);
        }
        const tree = appEditorStore.boardTree.find(node => node.data['data-id'] === connectedBoardId);
        tree.data.type = `${appEditorStore.connectedBoardInfoMap[connectedBoardId].isSet ? "CLIP_SET" : "CLIP"}`;
        delete tree.data.checked;
        appEditorStore.refreshComponentList();
        appEditorStore.editor.Commands.run('reload-scripts');
        return new UnlockCommonBoardStack(this.after, this.before);
    }
}

export class ChangeComponentOrderStack extends ActionStack {
    async cancel() {
        /** @type object */
        getAppEditorStore().setBoardTree(this.before);
        getAppEditorStore().refreshComponentList();
        getAppEditorStore().relocateHighlighter();
        getAppEditorStore().editor.Commands.run('reload-scripts');
        return new ChangeComponentOrderStack(this.after, this.before);
    }
}

export class SwiperAddStack extends ActionStack {
    async cancel() {
        const {connectedBoardId, selectorInfo} = this.after;
        const higherComponent = getAppEditorStore().componentList.find((component) => component.attributes.attributes['data-id'] === connectedBoardId);
        const deleteStackInfo = {
            connected: undefined,
            objImg: [],
            model: undefined
        }
        if (selectorInfo.connected) {
            const {selector, selectorIdx, parentSelector, parentIdx} = selectorInfo.connected;
            const parentComponents = higherComponent.find(parentSelector);
            const parentComponent = parentComponents[parentIdx];
            const components = parentComponent.find(selector);
            const component = components[selectorIdx];
            deleteStackInfo.connected = Object.assign(selectorInfo.connected, {html: component.toHTML()})
            component.destroy();
        }
        if (selectorInfo.objImg.length > 0) {
            for (const {selector, selectorIdx, parentSelector, parentIdx} of selectorInfo.objImg) {
                const parentComponents = higherComponent.find(parentSelector);
                const parentComponent = parentComponents[parentIdx];
                const components = parentComponent.find(selector);
                const component = components[selectorIdx];
                deleteStackInfo.objImg.push({
                    selector,
                    selectorIdx,
                    parentSelector,
                    parentIdx,
                    html: component.toHTML()
                })
                component.destroy();
            }
        }
        if (selectorInfo.model) {
            const {selector, selectorIdx, parentSelector, parentIdx} = selectorInfo.model;
            const parentComponents = higherComponent.find(parentSelector);
            const parentComponent = parentComponents[parentIdx];
            const components = parentComponent.find(selector);
            const component = components[selectorIdx];
            deleteStackInfo.model = Object.assign(selectorInfo.model, {html: component.toHTML()})
            let isColorBtnOn = false;
            if (component.getClasses().includes('on') && component.getClasses('color_btn')) {
                isColorBtnOn = true;
            }
            component.destroy();
            if (isColorBtnOn) {
                const firstCustomModel = parentComponent.getChildAt(0);
                if (firstCustomModel) {
                    firstCustomModel.parent().forEachChild((child) => child.removeClass('on'));
                    firstCustomModel.addClass('on');
                    if (firstCustomModel.view.el.attributes['connected-comp-id'] && firstCustomModel.view.el.attributes['connected-comp-id'].value) {
                        const connectedComponent = getAppEditorStore().editor.DomComponents.getById(firstCustomModel.view.el.attributes['connected-comp-id'].value);
                        connectedComponent.parent().forEachChild((child) => child.removeClass('on'));
                        connectedComponent.addClass('on');
                    }
                    if (firstCustomModel.view.el.attributes['obj-img-id'] && firstCustomModel.view.el.attributes['obj-img-id'].value) {
                        const objImgIdList = firstCustomModel.view.el.attributes['obj-img-id'].value.split(',');
                        for (const objImgId of objImgIdList) {
                            const objImgComponent = getAppEditorStore().editor.DomComponents.getById(objImgId);
                            objImgComponent.parent().forEachChild((child) => child.removeClass('on'));
                            objImgComponent.addClass('on');
                        }
                    }
                }
            }
        }
        getAppEditorStore().refreshComponentList();
        getAppEditorStore().editor.Commands.run('reload-scripts');
        return new SwiperDeleteStack({connectedBoardId, selectorInfo: deleteStackInfo}, this.before);
    }
}

export class SwiperDeleteStack extends ActionStack {
    async cancel() {
        const {connectedBoardId, selectorInfo} = this.before;
        const higherComponent = getAppEditorStore().componentList.find((component) => component.attributes.attributes['data-id'] === connectedBoardId);
        const addSelectorInfo = {
            connected: undefined,
            objImg: [],
            model: undefined
        }

        if (selectorInfo.connected && selectorInfo.connected.selector) {
            const {selectorIdx, parentSelector, parentIdx, html} = selectorInfo.connected;
            const parentComponents = higherComponent.find(parentSelector);
            const parentComponent = parentComponents[parentIdx];
            const newComponent = parentComponent.append(html, {at: selectorIdx})[0];
            newComponent.removeClass('on');
            addSelectorInfo.connected = getAppEditorStore().getComponentSelector(newComponent, true);
        }
        if (selectorInfo.objImg.length > 0) {
            for (const {selectorIdx, parentSelector, parentIdx, html} of selectorInfo.objImg) {
                const parentComponents = higherComponent.find(parentSelector);
                const parentComponent = parentComponents[parentIdx];
                const newComponent = parentComponent.append(html, {at: selectorIdx})[0];
                newComponent.removeClass('on');
                addSelectorInfo.objImg.push(getAppEditorStore().getComponentSelector(newComponent, true));
            }
        }
        if (selectorInfo.model) {
            const {selectorIdx, parentSelector, parentIdx, html} = selectorInfo.model;
            const parentComponents = higherComponent.find(parentSelector);
            const parentComponent = parentComponents[parentIdx];
            const newComponent = parentComponent.append(html, {at: selectorIdx})[0];
            newComponent.removeClass('on');
            addSelectorInfo.model = getAppEditorStore().getComponentSelector(newComponent, true);
        }
        getAppEditorStore().refreshComponentList();
        getAppEditorStore().editor.Commands.run('reload-scripts');
        console.log({connectedBoardId, selectorInfo: addSelectorInfo});
        return new SwiperAddStack(this.after, {connectedBoardId, selectorInfo: addSelectorInfo});
    }
}

export class SwiperStyleUpdateStack extends ActionStack {
    async cancel() {
        const {style} = this.before;
        const component = this.getTargetComponent(this.before);
        component.view.el.setAttribute('data-color', style);
        component.attributes.attributes['data-color'] = style;
        getAppEditorStore().editor.Commands.run('reload-scripts')
        return new SwiperStyleUpdateStack(this.after, this.before);
    }
}

export class StyleUpdateStack extends ActionStack {
    async cancel() {
        const {style, modelClasses} = this.before;
        const component = this.getTargetComponent(this.after);
        const nowClasses = component.getClasses();
        if (modelClasses.length !== nowClasses.length) {
            if (modelClasses.length > nowClasses.length) {
                modelClasses.filter(className => !nowClasses.includes(className)).forEach(cn => component.addClass(cn));
            } else {
                nowClasses.filter(className => !modelClasses.includes(className)).forEach(cn => component.removeClass(cn));
            }
        }
        const oneDepthComponent = findOneDepthParent(component);
        const styleMap = getAppEditorStore().getComponentCssRules(component);
        let styles = [];
        Object.values(styleMap).forEach(styleList => styles = styles.concat(styleList));
        styles.forEach(s => s.destroy());
        if (style && style.length > 0) {
            const cssRules = getAppEditorStore().editor.CssComposer.addRules(style);
            cssRules.forEach(cssRule => cssRule.attributes['component'] = getAppEditorStore().getComponentSelectorString(oneDepthComponent));
        }
        return new StyleUpdateStack(this.after, this.before)
    }
}

export class ImageUpdateStack extends ActionStack {
    async cancel() {
        const {imageSrc} = this.before;
        const component = this.getTargetComponent(this.before);
        component.set('src', imageSrc);
        return new ImageUpdateStack(this.after, this.before);
    }
}

export class HtmlTextUpdateStack extends ActionStack {
    async cancel() {
        /** @type {{html:string,selector:string,connectedBoardId:string}} */
        const {html} = this.before;
        const component = this.getTargetComponent(this.before);
        component.replaceWith(html)
        return new HtmlTextUpdateStack(this.after, this.before);
    }
}

export class PropertyUpdateStack extends ActionStack {
    async cancel() {
        const {href} = this.before;
        const component = this.getTargetComponent(this.before);
        const hrefTrait = component.getTraits()[2];
        hrefTrait.setValue(href);
        const inputField = document.getElementsByClassName('href-input-field')[0].getElementsByTagName('input')[0];
        if (inputField) {
            if (inputField.value === this.before.href || inputField.value === this.after.href) {
                inputField.value = this.before.href;
            }
        }
        return new PropertyUpdateStack(this.after, this.before);
    }
}

export class RTEActionStack extends ActionStack {
    async cancel() {
        const {html} = this.before;
        const component = this.getTargetComponent(this.before);
        const nowHtml = component.toHTML();
        component.replaceWith(html);
        this.before.html = nowHtml;
        return new RTEActionStack(this.before)
    }
}

export class TableUpdateStack extends ActionStack {
    async cancel() {
        const {html} = this.before;
        const component = this.getTargetComponent(this.before);
        component.replaceWith(html);
        return new TableUpdateStack(this.after, this.before);
    }
}

export class ConvertImageStack extends ActionStack {
    async cancel() {
        const {html} = this.before;
        let component = this.getTargetComponent(this.after);
        const parentComponent = component.parent();
        if (parentComponent.attributes.tagName === 'a') {
            component = parentComponent;
        }
        component.replaceWith(html);
        getAppEditorStore().editor.select(null)
        getAppEditorStore().editor.StyleManager.select(null);
        return new ConvertImageStack(this.after, this.before);
    }
}

export class GroupingClipboardsStack extends ActionStack {
    async cancel() {
        const addStack = new AddStack(null, this.after);
        const deleteStack = await addStack.cancel();
        const deleteMultiStack = new DeleteMultiStack(this.before, null);
        const addMultiStack = await deleteMultiStack.cancel();
        getAppEditorStore().refreshComponentList();
        getAppEditorStore().editor.Commands.run('reload-scripts');
        return new UngroupClipboardsStack(addMultiStack.after, deleteStack.before);
    }
}

export class UngroupClipboardsStack extends ActionStack {
    async cancel() {
        const addMultiStack = new AddMultiStack(null, this.before);
        const deleteMultiStack = await addMultiStack.cancel();
        const deleteStack = new DeleteStack(this.after, null);
        const addStack = await deleteStack.cancel();
        getAppEditorStore().refreshComponentList();
        getAppEditorStore().editor.Commands.run('reload-scripts');
        return new GroupingClipboardsStack(deleteMultiStack.before, addStack.after);
    }
}

export class ChangeConnectedBoardNameStack extends ActionStack {
    async cancel() {
        const {name, html} = this.before;
        const treeNode = getAppEditorStore().boardTree.find(tree => tree.text === this.after.name);
        if (html) {
            const component = getAppEditorStore().componentList.find((component, idx) => component.attributes.attributes['data-id'] === treeNode.data['data-id']);
            component.replaceWith(html);
        }
        getAppEditorStore().refreshComponentList();
        treeNode.text = name;
        return new ChangeConnectedBoardNameStack(this.after, this.before);
    }
}

export class AddComponentStack extends ActionStack {
    async cancel() {
        const {connectedBoardId, selectorInfo} = this.after;
        const higherComponent = getAppEditorStore().componentList.find((component) => component.attributes.attributes['data-id'] === connectedBoardId);
        const {selector, selectorIdx, parentSelector, parentIdx} = selectorInfo;
        const parentComponents = higherComponent.find(parentSelector);
        let parentComponent = parentComponents[parentIdx];
        if (!parentComponent) {
            parentComponent = higherComponent;
        }
        const components = parentComponent.find(selector);
        const component = components[selectorIdx];
        component.destroy();
        getAppEditorStore().refreshComponentList();
        getAppEditorStore().editor.Commands.run('reload-scripts');
        return new DeleteComponentStack(this.after, this.before);
    }
}

export class DeleteComponentStack extends ActionStack {
    async cancel() {
        const {connectedBoardId, selectorInfo} = this.before;
        const higherComponent = getAppEditorStore().componentList.find((component) => component.attributes.attributes['data-id'] === connectedBoardId);
        const {selectorIdx, parentSelector, parentIdx, html} = selectorInfo;
        const parentComponents = higherComponent.find(parentSelector);
        let parentComponent = parentComponents[parentIdx];
        if (!parentComponent) {
            parentComponent = higherComponent;
        }
        parentComponent.append(html, {at: selectorIdx});

        getAppEditorStore().refreshComponentList();
        getAppEditorStore().editor.Commands.run('reload-scripts');
        return new AddComponentStack(this.after, this.before);
    }
}

export default class UndoManager {
    /** @type {ActionStack[]} */
    undoStacks = [];
    /** @type {ActionStack[]} */
    redoStacks = [];

    constructor(store) {
        this.store = store;
    }


    add(stack) {
        this.redoStacks = [];
        this.undoStacks.push(stack);
    }

    clear() {
        this.undoStacks = [];
        this.redoStacks = [];
    }

    async undo() {
        if (getModalStore().MODAL_INIT_OPENED.length > 0) return;
        try {
            if (this.undoStacks.length === 0) return;
            const lastStack = this.undoStacks.pop();
            const redoStack = await lastStack.cancel();
            this.redoStacks.push(redoStack);
        } catch (e) {
            console.log(e);
        }

    }

    async redo() {
        if (getModalStore().MODAL_INIT_OPENED.length > 0) return;
        try {
            if (this.redoStacks.length === 0) return;
            const lastStack = this.redoStacks.pop();
            const undoStack = await lastStack.cancel();
            this.undoStacks.push(undoStack)
        } catch (e) {
            console.log(e);
        }
    }
}

export const useUndoManager = () => {
    return useStore().undoManager;
}

export const getUndoManager = () => {
    return getStore().undoManager;
}